import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api, { API_BASE } from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { useData } from "../../context/DataContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  FileText,
  ShieldCheck,
  Award,
  BookOpen,
  GraduationCap,
  ClipboardList,
  ClipboardPlus,
  FileSignature,
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  ChevronRight,
  Loader2,
  CalendarDays,
  Eye,
  Download,
  Plus,
  Search,
  X,
  Send,
  RotateCcw,
} from "lucide-react";
import {
  downloadFormSubmissionPdf,
  ONBOARDING_FORM_FIELD_ORDER,
} from "../../utils/onboarding-form-pdf";
import { generateContractPdf } from "../../utils/contract-pdf";
import {
  SignedContract,
  SignedContractDocument,
  formatContractDate,
} from "../../components/onboarding/SignedContractDocument";

// ===================================================================
// Interfaces
// ===================================================================
interface OnboardingRequestDetail {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  startDate: string | null;
  progress: number | string;
  status: string;
  formsStatus: string | null;
  policiesStatus: string | null;
  trainingStatus: string | null;
  isConvertedToUser: boolean;
  jobPosition: {
    jobRole: { roleName: string } | null;
  } | null;
  manager: {
    id: number;
    name: string;
    email?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface DetailForm {
  id: string;
  name: string;
  description: string | null;
  required: boolean;
  status: "Completed" | "In Progress" | "Not Started";
  progress: number;
  submittedAt: string | null;
}

interface DetailPolicy {
  id: string | number;
  title: string;
  description: string | null;
  acknowledged: boolean;
  acknowledgedAt: string | null;
}

interface DetailLicence {
  id: string;
  name: string;
  issuingAuthority: string | null;
  type: string | null;
  required: boolean;
  uploaded: boolean;
  fileName: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  uploadedAt: string | null;
  attachmentId: string | null;
  qualificationId: string | null;
}

interface DetailCourse {
  id: number;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  contentType: "SCORM" | "PDF" | "PPTX";
  estimatedDurationMinutes: number | null;
  issuesCertificate: boolean;
  status: "Assigned" | "In Progress" | "Completed";
  startedAt: string | null;
  completedAt: string | null;
  scorePercent: number | null;
  profile: {
    addedAt: string;
    assignmentId: number;
    certificateId: number | null;
    certificateNumber: string | null;
  } | null;
}

interface SupportContact {
  id: string;
  label: string;
  userId: number | null;
  user: {
    id: number;
    name: string;
    email: string;
    mobile: string | null;
  } | null;
}

interface TrainingPlanDetail {
  id: number;
  name: string;
  description: string | null;
  status: string;
  procedures: Array<{
    id: number;
    name: string;
    description: string | null;
    cadence: string;
    version: number;
  }>;
}

interface CardProgress {
  completed: number;
  total: number;
  progress: number;
}

interface ActivityItem {
  id: number;
  type: "form" | "policy" | "licence" | "created" | string;
  description: string;
  timestamp: string;
}

interface OnboardingDetailPayload {
  forms: DetailForm[];
  policies: DetailPolicy[];
  licences: DetailLicence[];
  learning: DetailCourse[];
  supportNetwork: SupportContact[];
  trainingPlans: TrainingPlanDetail[];
  cardProgress: {
    forms: CardProgress;
    policies: CardProgress;
    licences: CardProgress;
    learning: CardProgress;
    training: CardProgress;
  };
  activity: ActivityItem[];
  contract: SignedContract | null;
}

// ===================================================================
// Progress Ring Component
// ===================================================================
const ProgressRing: React.FC<{ progress: number; size?: number; strokeWidth?: number }> = ({
  progress,
  size = 80,
  strokeWidth = 6,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="white"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-white">{progress}%</span>
        <span className="text-[10px] text-white/70">Complete</span>
      </div>
    </div>
  );
};

// Avatar colours cycled by index for support network contacts
const CONTACT_COLORS = ["bg-blue-500", "bg-purple-500", "bg-green-500", "bg-orange-500", "bg-gray-500"];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

// ===================================================================
// Main Component
// ===================================================================
const OnboardingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState<OnboardingRequestDetail | null>(null);
  const [detail, setDetail] = useState<OnboardingDetailPayload | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedTrainingPlans, setExpandedTrainingPlans] = useState<Set<number>>(new Set());
  const [converting, setConverting] = useState(false);
  const [resending, setResending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [downloadingFormId, setDownloadingFormId] = useState<string | null>(null);
  const [viewingContract, setViewingContract] = useState(false);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const contractRef = useRef<HTMLDivElement>(null);
  const {
    qualificationTypes,
    fetchQualificationTypes,
    qualificationPresets,
    fetchQualificationPresets,
    selectionUsers,
  } = useData();
  const [registerLicence, setRegisterLicence] = useState<DetailLicence | null>(null);
  const [registerForm, setRegisterForm] = useState<{
    name: string;
    typeId: string;
    presetId: string;
    issuingAuthority: string;
    issueDate: string;
    expiryDate: string;
    requiresRenewal: boolean;
    reminderIntervals: number[];
    reminderRecipients: string[];
  }>({
    name: "",
    typeId: "",
    presetId: "",
    issuingAuthority: "",
    issueDate: "",
    expiryDate: "",
    requiresRenewal: false,
    reminderIntervals: [30, 14, 7],
    reminderRecipients: [],
  });
  const [customInterval, setCustomInterval] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [addingToProfileId, setAddingToProfileId] = useState<number | null>(null);

  const canView = ability.can(ACTIONS.READ, RESOURCES.ONBOARDING_REQUEST_DETAIL);
  const canConvert = ability.can(ACTIONS.UPDATE, RESOURCES.ONBOARDING_REQUEST_STATUS);
  const canUpdateQualification = ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION);
  const canCreateCourseAssignment = ability.can(ACTIONS.CREATE, RESOURCES.COURSE_ASSIGNMENT);
  const canReadPresets = ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_PRESET);
  const canReadPeople =
    ability.can(ACTIONS.READ, RESOURCES.PERSON_PAGE) ||
    ability.can(ACTIONS.READ, RESOURCES.PERSON_I_MANAGE_PAGE);

  // -----------------------------------------------------------------
  // Fetch onboarding request + detail payload (template content merged
  // with the hire's real progress, training plans and activity feed)
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!id) return;
    fetchOnboardingRequest();
  }, [id]);

  // Keep progress/status live while the page is open — the hire may be
  // completing forms, policies and licences in their portal right now.
  // Refetch on a 30s interval and whenever the tab regains focus. Silent
  // refreshes skip the loading spinner and error toasts.
  useEffect(() => {
    if (!id) return;
    const refresh = () => fetchOnboardingRequest({ silent: true });
    const interval = setInterval(refresh, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [id]);

  const fetchOnboardingRequest = async ({ silent = false }: { silent?: boolean } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get<{
        success: boolean;
        data: OnboardingRequestDetail;
        detail: OnboardingDetailPayload;
      }>(`/api/onboarding/${id}`);
      if (res.success && res.data) {
        setOnboarding(res.data);
        setDetail(res.detail || null);
      } else if (!silent) {
        toast.error("Failed to load onboarding request");
      }
    } catch (error) {
      console.error("Error fetching onboarding request:", error);
      if (!silent) toast.error("Failed to load onboarding request");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // -----------------------------------------------------------------
  // Convert to Active User
  // -----------------------------------------------------------------
  const handleConvertToUser = async () => {
    if (!onboarding) return;
    try {
      setConverting(true);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${id}/convert`,
        {}
      );
      if (res.success) {
        toast.success("User converted successfully");
        setOnboarding((prev) =>
          prev ? { ...prev, isConvertedToUser: true, status: "CONVERTED" } : null
        );
      } else {
        toast.error(res.message || "Failed to convert user");
      }
    } catch (error) {
      toast.error("Failed to convert user");
    } finally {
      setConverting(false);
    }
  };

  // -----------------------------------------------------------------
  // Push a completed onboarding course to the hire's learner profile.
  // Only valid once the hire is an active user; the server rejects it
  // otherwise (and when the course is not completed/part of the
  // template), so we surface its message instead of a generic one.
  // -----------------------------------------------------------------
  const handleAddCourseToProfile = async (course: DetailCourse) => {
    try {
      setAddingToProfileId(course.id);
      const res = await api.post<{
        success: boolean;
        message: string;
        data: {
          assignmentId: number;
          certificateId: number | null;
          certificateNumber: string | null;
          addedAt: string;
        };
      }>(`/api/onboarding/${id}/learning/${course.id}/add-to-profile`, {});
      if (res.success) {
        toast.success(res.message || `Added ${course.title} to the learner profile`);
        await fetchOnboardingRequest({ silent: true });
      } else {
        toast.error(res.message || "Failed to add course to learner profile");
      }
    } catch (error) {
      // api.ts already toasts the server error; single toast only.
      console.error("Error adding course to learner profile:", error);
    } finally {
      setAddingToProfileId(null);
    }
  };

  // -----------------------------------------------------------------
  // Resend the onboarding invite — re-issues the account-setup token and
  // re-sends the welcome email. Used when the hire's link expired (or they
  // never consumed it) and they still need to set a password / reach their
  // portal.
  // -----------------------------------------------------------------
  const handleResendInvite = async () => {
    if (!onboarding) return;
    try {
      setResending(true);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${id}/resend-invite`,
        {}
      );
      if (res.success) {
        toast.success(res.message || "Onboarding link resent");
      } else {
        toast.error(res.message || "Failed to resend the onboarding link");
      }
    } catch {
      // api.ts already toasts the server error; single toast only.
    } finally {
      setResending(false);
    }
  };

  // -----------------------------------------------------------------
  // Restore a cancelled hire — un-archives the account (Cancel archives it,
  // which blocks login), re-opens the request and re-sends the onboarding
  // link in one step so the hire can set a password and finish onboarding.
  // -----------------------------------------------------------------
  const handleRestoreInvite = async () => {
    if (!onboarding) return;
    try {
      setRestoring(true);
      const res = await api.post<{ success: boolean; message: string }>(
        `/api/onboarding/${id}/restore-invite`,
        {}
      );
      if (res.success) {
        toast.success(res.message || "Onboarding restored and link resent");
        setOnboarding((prev) =>
          prev ? { ...prev, status: "IN_PROGRESS" } : prev
        );
      } else {
        toast.error(res.message || "Failed to restore the onboarding request");
      }
    } catch {
      // api.ts already toasts the server error; single toast only.
    } finally {
      setRestoring(false);
    }
  };

  // -----------------------------------------------------------------
  // Add uploaded licence to the qualifications register. The hire's
  // portal upload already created the register record (linked to their
  // user account) — this dialog completes the mandatory fields on it,
  // so it only makes sense once the hire is an active user.
  // -----------------------------------------------------------------
  const openRegisterDialog = async (licence: DetailLicence) => {
    setRegisterLicence(licence);
    setCustomInterval("");
    setRecipientSearch("");
    const fallback = {
      name: licence.name,
      typeId: "",
      presetId: "",
      issuingAuthority: licence.issuingAuthority || "",
      issueDate: licence.issueDate ? String(licence.issueDate).split("T")[0] : "",
      expiryDate: licence.expiryDate ? String(licence.expiryDate).split("T")[0] : "",
      requiresRenewal: !!licence.expiryDate,
      reminderIntervals: [30, 14, 7],
      reminderRecipients: [] as string[],
    };
    setRegisterForm(fallback);
    if (qualificationTypes.length === 0) fetchQualificationTypes();
    if (canReadPresets && qualificationPresets.length === 0) fetchQualificationPresets();

    // Pre-fill from the actual register record (it may already carry a
    // type or HR-edited values) rather than the template defaults.
    setRegisterLoading(true);
    try {
      const res = await api.get<{
        success: boolean;
        qualification: {
          name: string;
          issuingAuthority: string | null;
          issueDate: string | null;
          expiryDate: string | null;
          requiresRenewal: boolean | null;
          reminderIntervals: number[] | null;
          reminderRecipients: string[] | null;
          type: { id: number | string } | null;
          preset: { id: string } | null;
        };
      }>(`/api/qualifications/${licence.qualificationId}`, { skipErrorToast: true });
      if (res.success && res.qualification) {
        const q = res.qualification;
        setRegisterForm({
          name: q.name || fallback.name,
          typeId: q.type?.id ? String(q.type.id) : "",
          presetId: q.preset?.id ? String(q.preset.id) : "",
          issuingAuthority: q.issuingAuthority || fallback.issuingAuthority,
          issueDate: q.issueDate ? String(q.issueDate).split("T")[0] : fallback.issueDate,
          expiryDate: q.expiryDate ? String(q.expiryDate).split("T")[0] : fallback.expiryDate,
          requiresRenewal: q.requiresRenewal || fallback.requiresRenewal,
          reminderIntervals:
            q.reminderIntervals && q.reminderIntervals.length > 0
              ? q.reminderIntervals
              : fallback.reminderIntervals,
          reminderRecipients: q.reminderRecipients || [],
        });

        // Mirror the Add New Qualification form: when the record carries no
        // reminder setup of its own, seed from the tenant-wide defaults.
        if ((!q.reminderRecipients || q.reminderRecipients.length === 0) && !q.requiresRenewal) {
          try {
            const defaultsRes = await api.get<{
              success: boolean;
              defaults: { defaultRecipients: string[]; defaultIntervals: number[] };
            }>("/api/qualifications/defaults", { skipErrorToast: true });
            if (defaultsRes.success && defaultsRes.defaults) {
              setRegisterForm((prev) => ({
                ...prev,
                reminderRecipients: defaultsRes.defaults.defaultRecipients || [],
                reminderIntervals:
                  defaultsRes.defaults.defaultIntervals?.length > 0
                    ? defaultsRes.defaults.defaultIntervals
                    : prev.reminderIntervals,
              }));
            }
          } catch (error) {
            console.error("Error loading qualification defaults:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error loading qualification record:", error);
    } finally {
      setRegisterLoading(false);
    }
  };

  // Applying a preset pre-populates the basic information; every field stays
  // editable so manual entry or tweaks are still possible.
  const handleApplyPreset = (presetId: string) => {
    const preset = qualificationPresets.find((p) => p.id === presetId);
    setRegisterForm((prev) => ({
      ...prev,
      presetId,
      name: preset?.name || prev.name,
      issuingAuthority: preset?.issuingAuthority || prev.issuingAuthority,
      typeId: preset?.type?.id ? preset.type.id.toString() : prev.typeId,
    }));
  };

  const addReminderInterval = () => {
    const val = parseInt(customInterval);
    if (!isNaN(val) && val > 0 && !registerForm.reminderIntervals.includes(val)) {
      setRegisterForm((prev) => ({
        ...prev,
        reminderIntervals: [...prev.reminderIntervals, val].sort((a, b) => b - a),
      }));
      setCustomInterval("");
    }
  };

  const handleRegisterSubmit = async () => {
    if (!registerLicence?.qualificationId) return;
    if (!registerForm.name.trim()) {
      toast.error("Qualification name is required");
      return;
    }
    if (!registerForm.typeId) {
      toast.error("Qualification type is required");
      return;
    }
    if (!registerForm.issuingAuthority.trim()) {
      toast.error("Issuing authority is required");
      return;
    }
    if (!registerForm.issueDate) {
      toast.error("Issue date is required");
      return;
    }

    try {
      setRegisterSaving(true);
      const res = await api.put<{ success: boolean; message?: string }>(
        `/api/qualifications/${registerLicence.qualificationId}`,
        {
          name: registerForm.name.trim(),
          typeId: registerForm.typeId,
          presetId: registerForm.presetId || null,
          issuingAuthority: registerForm.issuingAuthority.trim(),
          issueDate: registerForm.issueDate,
          expiryDate: registerForm.expiryDate || null,
          requiresRenewal: registerForm.requiresRenewal,
          reminderIntervals: registerForm.requiresRenewal ? registerForm.reminderIntervals : [],
          reminderRecipients: registerForm.requiresRenewal ? registerForm.reminderRecipients : [],
        }
      );
      if (res.success) {
        toast.success(`${registerForm.name} added to the qualifications register`);
        setRegisterLicence(null);
        fetchOnboardingRequest({ silent: true });
      } else {
        toast.error(res.message || "Failed to add qualification to register");
      }
    } catch (error) {
      toast.error("Failed to add qualification to register");
    } finally {
      setRegisterSaving(false);
    }
  };

  // -----------------------------------------------------------------
  // Download a completed form as a PDF of the submitted answers
  // -----------------------------------------------------------------
  const handleDownloadForm = async (form: DetailForm) => {
    if (!id) return;
    try {
      setDownloadingFormId(form.id);
      const res = await api.get<{
        success: boolean;
        data: {
          firstName: string;
          lastName: string;
          formData: Record<string, any>;
          isCompleted: boolean;
          submittedAt: string | null;
        };
      }>(`/api/onboarding/${id}/forms/${form.id}/submission`);
      if (res.success && res.data) {
        downloadFormSubmissionPdf({
          formName: form.name,
          employeeName: `${res.data.firstName} ${res.data.lastName}`.trim(),
          submittedAt: res.data.submittedAt,
          data: res.data.formData,
          fieldOrder: ONBOARDING_FORM_FIELD_ORDER[form.id],
        });
      } else {
        toast.error("Failed to download form");
      }
    } catch (error) {
      console.error("Error downloading form:", error);
      toast.error("Failed to download form");
    } finally {
      setDownloadingFormId(null);
    }
  };

  // -----------------------------------------------------------------
  // Open the completed form in a new tab. The ?requestId= query param
  // switches the form page into HR preview mode (read-only, loads the
  // hire's submission instead of the caller's own portal data).
  // -----------------------------------------------------------------
  const handleViewForm = (form: DetailForm) => {
    const formPath =
      form.id === "new-starter-form"
        ? "/my-onboarding/new-starter-form"
        : `/my-onboarding/forms/${form.id}`;
    window.open(`${formPath}?requestId=${id}`, "_blank");
  };

  // -----------------------------------------------------------------
  // Download the signed contract as a PDF of the rendered document. The
  // document mounts off-screen while the capture runs (same html2pdf
  // pipeline as the recruitment Offer view), then unmounts — no UI flash.
  // -----------------------------------------------------------------
  const handleDownloadContract = () => {
    if (!detail?.contract) return;
    setDownloadingContract(true);
  };

  useEffect(() => {
    const contract = detail?.contract;
    if (!downloadingContract || !contract || !contractRef.current) return;
    const node = contractRef.current;
    const safeName = (contract.candidateName || "Employment")
      .replace(/[^a-z0-9\- ]/gi, "")
      .trim();
    generateContractPdf(node, `Contract-${safeName || "Employment"}.pdf`)
      .catch((error) => {
        console.error("Failed to generate contract PDF:", error);
        toast.error("Failed to download contract");
      })
      .finally(() => setDownloadingContract(false));
  }, [downloadingContract, detail]);

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------
  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      NOT_STARTED: { label: "Not Started", className: "bg-gray-100 text-gray-700 border-gray-200" },
      IN_PROGRESS: { label: "Pre-Start In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
      COMPLETED: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      CONVERTED: { label: "Converted to Active User", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      CANCELLED: { label: "Cancelled", className: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || { label: status, className: "bg-gray-100 text-gray-700" };
    return <Badge variant="outline" className={`${s.className} font-medium`}>{s.label}</Badge>;
  };

  const getFormStatusBadge = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      Completed: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      "In Progress": { label: "In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
      "Not Started": { label: "Not Started", className: "bg-gray-50 text-gray-600 border-gray-200" },
    };
    const s = map[status] || { label: status, className: "bg-gray-50 text-gray-600" };
    return <Badge variant="outline" className={`${s.className} text-xs font-medium`}>{s.label}</Badge>;
  };

  const getCourseStatusBadge = (status: DetailCourse["status"]) => {
    const map: Record<string, { label: string; className: string }> = {
      Assigned: { label: "Assigned", className: "bg-gray-50 text-gray-600 border-gray-200" },
      "In Progress": { label: "In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
      Completed: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    };
    const s = map[status] || { label: status, className: "bg-gray-50 text-gray-600" };
    return <Badge variant="outline" className={`${s.className} text-xs font-medium`}>{s.label}</Badge>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Not set";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "form":
        return { icon: FileText, className: "bg-blue-50 text-blue-600" };
      case "policy":
        return { icon: ShieldCheck, className: "bg-emerald-50 text-emerald-600" };
      case "licence":
        return { icon: Award, className: "bg-amber-50 text-amber-600" };
      case "course":
        return { icon: GraduationCap, className: "bg-purple-50 text-purple-600" };
      default:
        return { icon: ClipboardList, className: "bg-gray-100 text-gray-500" };
    }
  };

  // -----------------------------------------------------------------
  // Tabs
  // -----------------------------------------------------------------
  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "forms", label: "Forms" },
    { id: "policies", label: "Policies" },
    { id: "learning", label: "Learning" },
    { id: "training", label: "Training Plan" },
    { id: "licences", label: "Licences & Qualifications" },
    { id: "activity", label: "Activity Feed" },
  ];

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  if (!canView) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!onboarding) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p>Onboarding request not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/onboarding")}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const fullName = `${onboarding.firstName} ${onboarding.lastName}`.trim();
  const initials = `${onboarding.firstName?.[0] || ""}${onboarding.lastName?.[0] || ""}`;
  const overallProgress = Math.round(Number(onboarding.progress) || 0);

  const forms = detail?.forms ?? [];
  const contract = detail?.contract ?? null;
  const policies = detail?.policies ?? [];
  const licences = detail?.licences ?? [];
  const learning = detail?.learning ?? [];
  const supportNetwork = detail?.supportNetwork ?? [];
  const trainingPlans = detail?.trainingPlans ?? [];
  const activity = detail?.activity ?? [];

  const progressCards = [
    {
      label: "Forms",
      card: detail?.cardProgress.forms,
      icon: FileText,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      label: "Policies",
      card: detail?.cardProgress.policies,
      icon: ShieldCheck,
      iconColor: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      label: "Licences",
      card: detail?.cardProgress.licences,
      icon: Award,
      iconColor: "text-amber-600",
      bgColor: "bg-amber-50",
    },
    {
      label: "Learning",
      card: detail?.cardProgress.learning,
      icon: BookOpen,
      iconColor: "text-indigo-600",
      bgColor: "bg-indigo-50",
    },
    {
      label: "Training",
      card: detail?.cardProgress.training,
      icon: GraduationCap,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50",
    },
  ];

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate("/onboarding")}
          className="flex items-center gap-1 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Onboarding
        </button>
        <ChevronRight className="w-3 h-3" />
        <span className="text-gray-900 font-medium">{fullName}</span>
      </div>

      {/* Title + CTA */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Onboarding Detail</h1>
        <div className="flex items-center gap-2">
          {canConvert && onboarding.status !== "CANCELLED" && (
            <Button
              variant="outline"
              onClick={handleResendInvite}
              disabled={resending || converting}
            >
              {resending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Resend Onboarding Link
            </Button>
          )}
          {canConvert && onboarding.status === "CANCELLED" && (
            <Button
              variant="outline"
              onClick={handleRestoreInvite}
              disabled={restoring || converting}
            >
              {restoring ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Restore &amp; Resend Onboarding Link
            </Button>
          )}
          {canConvert && !onboarding.isConvertedToUser && onboarding.status === "COMPLETED" && (
            <Button
              onClick={handleConvertToUser}
              disabled={converting}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              {converting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <User className="w-4 h-4 mr-2" />
              )}
              Convert to Active User
            </Button>
          )}
          {onboarding.isConvertedToUser && (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Converted to User
            </Badge>
          )}
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => window.open(`/my-onboarding?requestId=${id}`, "_blank")}
          className="flex items-start gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <ExternalLink className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">View Onboarding Worker Portal</p>
            <p className="text-xs text-gray-500 mt-0.5">Access the new hire&apos;s onboarding view</p>
          </div>
        </button>

        <button
          onClick={() => navigate(`/onboarding/${id}/hr-finance`)}
          className="flex items-start gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">New Hire Provisioning Tasks</p>
            <p className="text-xs text-gray-500 mt-0.5">Provisioning tasks for IT, HR and Finance</p>
          </div>
        </button>
      </div>

      {/* Hero Banner */}
      <div className="bg-slate-700 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-slate-500 flex items-center justify-center text-lg font-bold">
              {initials}
            </div>
            <div>
              <h2 className="text-xl font-bold">{fullName}</h2>
              <p className="text-sm text-slate-300">
                {onboarding.jobPosition?.jobRole?.roleName || "Role not set"}
                {onboarding.jobPosition?.jobRole?.roleName && " - Operations"}
              </p>
              <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                {onboarding.manager && (
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    Manager: {onboarding.manager.name}
                  </span>
                )}
                {onboarding.startDate && (
                  <span className="flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Starts {formatDate(onboarding.startDate)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ProgressRing progress={overallProgress} size={72} strokeWidth={5} />
            {getStatusBadge(onboarding.status)}
          </div>
        </div>
      </div>

      {/* Progress Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {progressCards.map(({ label, card, icon: Icon, iconColor, bgColor }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
                <span className="text-sm font-medium text-gray-700">{label}</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {card ? `${card.completed}/${card.total}` : "—"}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-800 rounded-full"
                style={{ width: `${card?.progress ?? 0}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">{card?.progress ?? 0}% complete</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Forms Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <h3 className="text-sm font-semibold text-gray-900">Forms</h3>
              </div>
              <button
                onClick={() => setActiveTab("forms")}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                View All
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {forms.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-400 text-center">
                  No forms assigned by the onboarding template
                </p>
              ) : (
                forms.map((form) => (
                  <div key={form.id} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      {form.status === "Completed" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : form.status === "In Progress" ? (
                        <Clock className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300" />
                      )}
                      <span className="text-sm text-gray-700">{form.name}</span>
                    </div>
                    {getFormStatusBadge(form.status)}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Policies Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-900">Policies</h3>
              </div>
              <button
                onClick={() => setActiveTab("policies")}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                View All
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {policies.length === 0 ? (
                <p className="px-5 py-6 text-sm text-gray-400 text-center">
                  No policies assigned by the onboarding template
                </p>
              ) : (
                policies.map((policy) => (
                  <div key={policy.id} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      {policy.acknowledged ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Circle className="w-4 h-4 text-gray-300" />
                      )}
                      <span className="text-sm text-gray-700">{policy.title}</span>
                    </div>
                    {policy.acknowledged ? (
                      <Badge
                        variant="outline"
                        className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium"
                      >
                        Signed
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-500">Pending</span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Support Network Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden lg:col-span-2">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
              <User className="w-4 h-4 text-indigo-600" />
              <h3 className="text-sm font-semibold text-gray-900">Support Network</h3>
            </div>
            <div className="p-5">
              {supportNetwork.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-2">
                  No support network declared in the onboarding template
                </p>
              ) : (
                <div className="flex flex-wrap gap-6">
                  {supportNetwork.map((contact, index) => {
                    const displayName = contact.user?.name || contact.label;
                    return (
                      <div key={contact.id} className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-full ${CONTACT_COLORS[index % CONTACT_COLORS.length]} flex items-center justify-center text-white text-xs font-bold`}
                        >
                          {getInitials(displayName)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{displayName}</p>
                          <p className="text-xs text-gray-500">{contact.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== FORMS TAB ===== */}
      {activeTab === "forms" && (
        <div className="space-y-4">
          {forms.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No forms assigned</h3>
              <p className="text-xs text-gray-500">
                The selected onboarding template has no forms.
              </p>
            </div>
          ) : (
            forms.map((form) => (
              <div
                key={form.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{form.name}</h3>
                      {form.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{form.description}</p>
                      )}
                      {form.status === "Completed" && form.submittedAt && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Completed {formatDate(form.submittedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {form.status === "Not Started" ? (
                      <span className="text-xs text-gray-500">Not Started</span>
                    ) : (
                      getFormStatusBadge(form.status)
                    )}
                    {form.status === "Completed" && (
                      <>
                        <button
                          onClick={() => handleViewForm(form)}
                          title="View completed form"
                          aria-label={`View ${form.name}`}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadForm(form)}
                          disabled={downloadingFormId === form.id}
                          title="Download completed form"
                          aria-label={`Download ${form.name}`}
                          className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {downloadingFormId === form.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gray-900 rounded-full transition-all"
                      style={{ width: `${form.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1.5">{form.progress}% complete</p>
                </div>
              </div>
            ))
          )}

          {/* Signed Contract — completed during Recruitment, read-only for
              the hire. Always shown to HR/Finance as the last form so the
              contract details stay accessible (candidate sees it in their
              portal too). */}
          {contract && contract.signed && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0 mt-0.5">
                    <FileSignature className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Signed Contract</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Signed{" "}
                      {formatContractDate(contract.candidateSignedDate || contract.acceptedAt)}
                      {contract.positionTitle ? ` · ${contract.positionTitle}` : ""}
                      {contract.companyName ? ` · ${contract.companyName}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge
                    variant="outline"
                    className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium"
                  >
                    Signed
                  </Badge>
                  <button
                    onClick={() => setViewingContract(true)}
                    title="View signed contract"
                    aria-label="View signed contract"
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View
                  </button>
                  <button
                    onClick={handleDownloadContract}
                    disabled={downloadingContract}
                    title="Download signed contract"
                    aria-label="Download signed contract"
                    className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {downloadingContract ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Progress bar — always complete; no candidate action needed */}
              <div className="mt-4">
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gray-900 rounded-full" style={{ width: "100%" }} />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  100% complete · Completed during recruitment — no action required
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== POLICIES TAB ===== */}
      {activeTab === "policies" && (
        <div className="space-y-3">
          {policies.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No policies assigned</h3>
              <p className="text-xs text-gray-500">
                The selected onboarding template has no policies.
              </p>
            </div>
          ) : (
            policies.map((policy) => (
              <div
                key={policy.id}
                className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3">
                  {policy.acknowledged ? (
                    <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center shrink-0" />
                  )}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{policy.title}</h3>
                    {policy.acknowledged && policy.acknowledgedAt && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Acknowledged {formatDate(policy.acknowledgedAt)}
                      </p>
                    )}
                  </div>
                </div>
                {policy.acknowledged ? (
                  <Badge
                    variant="outline"
                    className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium shrink-0"
                  >
                    Signed
                  </Badge>
                ) : (
                  <span className="text-xs text-gray-500 shrink-0">Pending</span>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* ===== LEARNING TAB ===== */}
      {activeTab === "learning" && (
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Learning</h2>
            <p className="text-sm text-gray-500 mt-1">
              Idarah Learning courses from the onboarding template. They do not block
              conversion — once a course is complete, add it to the learner profile here.
            </p>
          </div>

          {learning.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No courses assigned</h3>
              <p className="text-xs text-gray-500">
                The selected onboarding template has no courses.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {learning.map((course) => (
                <div
                  key={course.id}
                  className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    {course.thumbnailUrl ? (
                      <img
                        src={course.thumbnailUrl}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover shrink-0 bg-gray-100 border border-gray-100"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-indigo-50 flex flex-col items-center justify-center shrink-0 gap-0.5">
                        <BookOpen className="w-6 h-6 text-indigo-500" />
                        <span className="text-[10px] font-medium text-indigo-500">
                          {course.contentType}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{course.title}</h3>
                        {getCourseStatusBadge(course.status)}
                      </div>
                      {course.description && (
                        <p className="text-xs text-gray-500 mt-1">{course.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge
                          variant="outline"
                          className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                        >
                          {course.contentType}
                        </Badge>
                        {course.estimatedDurationMinutes != null && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            {course.estimatedDurationMinutes} min
                          </span>
                        )}
                        {course.issuesCertificate && (
                          <Badge
                            variant="outline"
                            className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
                          >
                            <Award className="w-3 h-3 mr-1" />
                            Certificate
                          </Badge>
                        )}
                      </div>
                      {course.status === "Completed" && (
                        <p className="text-xs text-gray-500 mt-1.5">
                          {course.scorePercent != null && (
                            <>Score: {Math.round(course.scorePercent)}% · </>
                          )}
                          Completed {formatDate(course.completedAt)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {course.profile ? (
                      <div className="flex flex-col items-end gap-1">
                        <Badge
                          variant="outline"
                          className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Added to learner profile
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatDate(course.profile.addedAt)}
                        </span>
                      </div>
                    ) : course.status === "Completed" ? (
                      /* Pushing the course onto the hire's learner profile only
                         works once they are an active user — mirror the
                         licences tab's pending treatment until then. */
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs font-medium"
                        disabled={
                          !onboarding.isConvertedToUser ||
                          !canCreateCourseAssignment ||
                          addingToProfileId === course.id
                        }
                        title={
                          !onboarding.isConvertedToUser
                            ? "Available once the hire is converted to an active user"
                            : !canCreateCourseAssignment
                              ? "You do not have permission to add courses to a learner profile"
                              : "Add this course to the hire's learner profile"
                        }
                        onClick={() => handleAddCourseToProfile(course)}
                      >
                        {addingToProfileId === course.id ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <ClipboardPlus className="w-3.5 h-3.5 mr-1" />
                        )}
                        Add to Learner Profile
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500">Pending</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== TRAINING PLAN TAB ===== */}
      {activeTab === "training" && (
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Training Plan</h2>
            <p className="text-sm text-gray-500 mt-1">
              The training plans assigned to this employee&apos;s onboarding journey
            </p>
          </div>

          {trainingPlans.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No training plans assigned</h3>
              <p className="text-xs text-gray-500">
                No training plans were selected when hiring was commenced.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {trainingPlans.map((plan, planIndex) => {
                const isExpanded = expandedTrainingPlans.has(plan.id);
                return (
                  <div key={plan.id} className={planIndex > 0 ? "border-t border-gray-100" : ""}>
                    {/* Plan Header */}
                    <button
                      onClick={() => {
                        setExpandedTrainingPlans((prev) => {
                          const next = new Set(prev);
                          if (next.has(plan.id)) {
                            next.delete(plan.id);
                          } else {
                            next.add(plan.id);
                          }
                          return next;
                        });
                      }}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-900">{plan.name}</span>
                        <Badge
                          variant="outline"
                          className={
                            plan.status === "Completed"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                              : plan.status === "In Progress"
                              ? "bg-blue-50 text-blue-700 border-blue-200 text-xs"
                              : "bg-gray-50 text-gray-600 border-gray-200 text-xs"
                          }
                        >
                          {plan.status}
                        </Badge>
                      </div>
                      <ChevronRight
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                      />
                    </button>

                    {/* Plan Procedures */}
                    {isExpanded && (
                      <div className="border-t border-gray-100">
                        <div className="px-5 py-2 bg-gray-50/70">
                          <span className="text-xs font-semibold text-gray-500 tracking-wide uppercase">
                            Procedures ({plan.procedures.length})
                          </span>
                        </div>
                        {plan.procedures.length === 0 ? (
                          <p className="px-5 py-4 text-sm text-gray-400">
                            This training plan has no procedures.
                          </p>
                        ) : (
                          <div className="divide-y divide-gray-50">
                            {plan.procedures.map((procedure) => (
                              <div
                                key={procedure.id}
                                className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-gray-50/30 transition-colors"
                              >
                                <div>
                                  <p className="text-sm text-gray-700">{procedure.name}</p>
                                  {procedure.description && (
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                      {procedure.description}
                                    </p>
                                  )}
                                </div>
                                {procedure.cadence && (
                                  <Badge
                                    variant="outline"
                                    className="bg-gray-50 text-gray-600 border-gray-200 text-xs shrink-0"
                                  >
                                    {procedure.cadence}
                                  </Badge>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ===== LICENCES TAB ===== */}
      {activeTab === "licences" && (
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Licences & Qualifications</h2>
            <p className="text-sm text-gray-500 mt-1">
              Licences and qualifications required by the onboarding template, and what {onboarding.firstName} has uploaded
            </p>
          </div>

          {/* Licence Cards */}
          {licences.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Award className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No licences required</h3>
              <p className="text-xs text-gray-500">
                The selected onboarding template has no licences or qualifications.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {licences.map((licence) => (
                <div
                  key={licence.id}
                  className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start gap-3">
                    {licence.uploaded ? (
                      <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Award className="w-5 h-5 text-amber-600" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-50 flex items-center justify-center shrink-0 mt-0.5">
                        <Clock className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">{licence.name}</h3>
                        {!licence.required && (
                          <Badge
                            variant="outline"
                            className="bg-gray-50 text-gray-600 border-gray-200 text-xs"
                          >
                            Optional
                          </Badge>
                        )}
                      </div>
                      {licence.uploaded ? (
                        <>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {licence.issuingAuthority && <>Issued by {licence.issuingAuthority} · </>}
                            Uploaded {formatDate(licence.uploadedAt)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Expiry: {licence.expiryDate ? formatDate(licence.expiryDate) : "No Expiry"}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {licence.issuingAuthority ? `Issued by ${licence.issuingAuthority} · ` : ""}
                          Awaiting upload from employee portal
                        </p>
                      )}
                    </div>
                  </div>
                  {licence.uploaded ? (
                    <div className="flex items-center gap-2 shrink-0">
                      {/* Register the upload as a proper qualification — only
                          once the hire is an active user, since the record is
                          tied to their user account. */}
                      {onboarding.isConvertedToUser && licence.qualificationId && canUpdateQualification && (
                        <button
                          onClick={() => openRegisterDialog(licence)}
                          title="Add to Qualifications Register"
                          aria-label={`Add ${licence.name} to qualifications register`}
                          className="flex items-center px-2 py-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                        >
                          <ClipboardPlus className="w-4 h-4" />
                        </button>
                      )}
                      {licence.attachmentId ? (
                        <>
                          <a
                            href={`${API_BASE}/api/qualifications/attachments/${licence.attachmentId}/download`}
                            title={`Download ${licence.fileName || "attachment"}`}
                            aria-label={`Download ${licence.name}`}
                            className="flex items-center px-2 py-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <a
                            href={`${API_BASE}/api/qualifications/attachments/${licence.attachmentId}/download?mode=view`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View
                          </a>
                        </>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium"
                        >
                          Uploaded
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-gray-50 text-gray-600 border-gray-200 text-xs font-medium shrink-0"
                    >
                      Pending
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== ACTIVITY FEED TAB ===== */}
      {activeTab === "activity" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <ClipboardList className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Recent Activity</h3>
          </div>
          {activity.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm font-medium text-gray-500">No activity yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Actions taken by the new hire will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {activity.map((item) => {
                const { icon: Icon, className } = getActivityIcon(item.type);
                return (
                  <div key={item.id} className="flex items-center gap-3 px-5 py-3.5">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${className}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{item.description}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(item.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* ===== SIGNED CONTRACT SHEET ===== */}
      <Sheet open={viewingContract} onOpenChange={(open) => !open && setViewingContract(false)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-emerald-600" />
              Signed Contract
            </SheetTitle>
            <SheetDescription>
              Signed by {contract?.candidateName || "the candidate"} on{" "}
              {contract ? formatContractDate(contract.candidateSignedDate || contract.acceptedAt) : "—"}{" "}
              — completed during recruitment.
            </SheetDescription>
          </SheetHeader>
          {contract && (
            <div className="px-4 py-2">
              <SignedContractDocument contract={contract} />
            </div>
          )}
          <SheetFooter>
            <Button variant="outline" onClick={() => setViewingContract(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Off-screen host for the contract PDF capture — mounted only while a
          download runs, so the capture never appears on screen. Mirrors the
          recruitment Offer letter PDF: the captured node is a plain block
          with an explicit pixel width inside a fixed off-screen wrapper. */}
      {contract && downloadingContract && (
        <div
          style={{ position: "fixed", left: "-10000px", top: 0, zIndex: -1, width: "794px" }}
          aria-hidden="true"
        >
          <style>{`
            .contract-letter-pdf p { margin: 0; padding: 0; }
            .contract-letter-pdf p + p { margin-top: 0.5em; }
            .contract-letter-pdf br { display: block; content: ""; margin-top: 0.5em; }
            .contract-letter-pdf ul, .contract-letter-pdf ol { margin: 0.5em 0; padding-left: 1.5em; }
            .contract-letter-pdf h1, .contract-letter-pdf h2, .contract-letter-pdf h3,
            .contract-letter-pdf h4, .contract-letter-pdf h5, .contract-letter-pdf h6 { margin: 0.5em 0; font-weight: 600; }
          `}</style>
          <div ref={contractRef}>
            <SignedContractDocument contract={contract} bare />
          </div>
        </div>
      )}

      {/* ===== ADD TO QUALIFICATIONS REGISTER SHEET ===== */}
      <Sheet open={!!registerLicence} onOpenChange={(open) => !open && setRegisterLicence(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Add to Qualifications Register</SheetTitle>
            <SheetDescription>
              Complete the mandatory register fields for {registerLicence?.name}. The document{" "}
              {fullName} uploaded is already attached to this record.
            </SheetDescription>
          </SheetHeader>

          {registerLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : (
            <div className="space-y-5 px-4 py-2">
              {/* Preset picker — same as the Add New Qualification form.
                  Selecting one pre-populates the basic information below;
                  every field remains editable for manual entry. */}
              {canReadPresets && (
                <div className="space-y-2">
                  <Label>Select Qualification Type</Label>
                  <p className="text-xs text-gray-500">Choose from preset list or enter custom</p>
                  <Select value={registerForm.presetId} onValueChange={handleApplyPreset}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a qualification." />
                    </SelectTrigger>
                    <SelectContent>
                      {qualificationPresets.map((preset) => (
                        <SelectItem key={preset.id} value={preset.id}>
                          {preset.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="reg-name">
                  Qualification Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="reg-name"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  placeholder="e.g. High Risk Work License"
                />
              </div>

              <div className="space-y-2">
                <Label>
                  Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={registerForm.typeId}
                  onValueChange={(value) => setRegisterForm({ ...registerForm, typeId: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {qualificationTypes
                      .filter((t) => !t.isArchived || t.id.toString() === registerForm.typeId)
                      .map((t) => (
                        <SelectItem key={t.id} value={t.id.toString()}>
                          {t.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-authority">
                  Issuing Authority <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="reg-authority"
                  value={registerForm.issuingAuthority}
                  onChange={(e) => setRegisterForm({ ...registerForm, issuingAuthority: e.target.value })}
                  placeholder="e.g. SafeWork NSW"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="reg-issue">
                    Issue Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="reg-issue"
                    type="date"
                    value={registerForm.issueDate}
                    onChange={(e) => setRegisterForm({ ...registerForm, issueDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-expiry">Expiry Date</Label>
                  <Input
                    id="reg-expiry"
                    type="date"
                    value={registerForm.expiryDate}
                    onChange={(e) => setRegisterForm({ ...registerForm, expiryDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Requires Renewal — same expand/collapse pattern as the
                  Add New Qualification form: toggling reveals the reminder
                  schedule and recipient options. */}
              <div className="space-y-4 rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="reg-renewal" className="text-sm font-semibold text-gray-900">
                      Requires Renewal
                    </Label>
                    <p className="text-xs text-gray-500">Enable automatic reminders before expiry</p>
                  </div>
                  <Switch
                    id="reg-renewal"
                    checked={registerForm.requiresRenewal}
                    onCheckedChange={(checked) =>
                      setRegisterForm({ ...registerForm, requiresRenewal: checked })
                    }
                    className="data-[state=checked]:bg-gray-900"
                  />
                </div>

                {registerForm.requiresRenewal && (
                  <div className="space-y-5 pt-4 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-300">
                    {/* Reminder schedule */}
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold text-gray-900">
                        Reminder Schedule (days before expiry)
                      </Label>
                      <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg min-h-[52px]">
                        {registerForm.reminderIntervals.map((days) => (
                          <div
                            key={days}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-md shadow-sm"
                          >
                            <span className="text-sm font-medium text-gray-700">{days} days</span>
                            <button
                              type="button"
                              onClick={() =>
                                setRegisterForm({
                                  ...registerForm,
                                  reminderIntervals: registerForm.reminderIntervals.filter(
                                    (d) => d !== days
                                  ),
                                })
                              }
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              aria-label={`Remove ${days} day reminder`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                        {registerForm.reminderIntervals.length === 0 && (
                          <span className="text-sm text-gray-400 self-center px-1 italic">
                            No reminders scheduled
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Input
                          placeholder="Add custom days"
                          type="number"
                          className="h-10 flex-1"
                          value={customInterval}
                          onChange={(e) => setCustomInterval(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addReminderInterval();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="h-10 shrink-0"
                          onClick={addReminderInterval}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>

                    {/* Reminder recipients */}
                    {canReadPeople && (
                      <div className="space-y-3 pt-4 border-t border-gray-100">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-semibold text-gray-900">
                            Send Reminders To
                          </Label>
                          <p className="text-xs text-gray-500">
                            Select users who should receive reminder notifications
                          </p>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Input
                            placeholder="Search recipients by name or email..."
                            className="pl-10 h-10"
                            value={recipientSearch}
                            onChange={(e) => setRecipientSearch(e.target.value)}
                          />
                          {recipientSearch && (
                            <button
                              type="button"
                              onClick={() => setRecipientSearch("")}
                              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                              aria-label="Clear recipient search"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                          {selectionUsers
                            .filter((u) => {
                              const search = recipientSearch.toLowerCase();
                              return (
                                u.name.toLowerCase().includes(search) ||
                                u.email.toLowerCase().includes(search)
                              );
                            })
                            .map((user) => (
                              <label
                                key={user.id}
                                htmlFor={`reg-recipient-${user.id}`}
                                className="flex items-start space-x-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                              >
                                <Checkbox
                                  id={`reg-recipient-${user.id}`}
                                  checked={registerForm.reminderRecipients.includes(
                                    user.id.toString()
                                  )}
                                  onCheckedChange={(checked) => {
                                    const userId = user.id.toString();
                                    const next = checked
                                      ? registerForm.reminderRecipients.includes(userId)
                                        ? registerForm.reminderRecipients
                                        : [...registerForm.reminderRecipients, userId]
                                      : registerForm.reminderRecipients.filter((id) => id !== userId);
                                    setRegisterForm({ ...registerForm, reminderRecipients: next });
                                  }}
                                  className="mt-0.5"
                                />
                                <div className="flex flex-col min-w-0">
                                  <span className="text-sm font-medium text-gray-900">
                                    {user.name}
                                  </span>
                                  <span className="text-xs text-gray-500 truncate">
                                    {user.email}
                                  </span>
                                </div>
                              </label>
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 italic">
                          {registerForm.reminderRecipients.length}{" "}
                          {registerForm.reminderRecipients.length === 1 ? "user" : "users"} selected
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* The hire's uploaded document satisfies the mandatory
                  attachment requirement — show it for reference. */}
              {registerLicence?.attachmentId && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                    <span className="text-sm text-gray-700 truncate">
                      {registerLicence.fileName || "Uploaded document"}
                    </span>
                  </div>
                  <a
                    href={`${API_BASE}/api/qualifications/attachments/${registerLicence.attachmentId}/download?mode=view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 shrink-0"
                  >
                    View
                  </a>
                </div>
              )}
            </div>
          )}

          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => setRegisterLicence(null)}
              disabled={registerSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegisterSubmit}
              disabled={registerSaving || registerLoading}
              className="bg-gray-900 hover:bg-gray-800 text-white"
            >
              {registerSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add to Register
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default OnboardingDetail;